import clsx from 'clsx'
import renderInputField from 'components/FormControls/textInput'
import { Icon, Tooltip } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Field, Form, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import * as Yup from 'yup'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import LoginField from 'modules/Forms/Account/Create/subcomponents/LoginField'
import SubmitButtons from 'modules/Forms/Account/Create/subcomponents/SubmitButtons'
import formStyles from 'modules/Forms/common-form.module.scss'
import i18n from 'i18next'
import emailValidator from 'utils/validators/emailValidator'
import tinValidator from 'utils/validators/tinValidator'
import { numberWithSpace } from 'utils/numberWithSpace'
import { isRequiredField } from 'utils/isRequiredField'

const BUDGET_MIN = 15000
const RECOMMENDED_BUDGET = 300000

export const SberAdsSchema = () =>
  Yup.object().shape({
    logins: Yup.array().of(Yup.object()),
    login: Yup.string().when('logins', ([logins], schema) =>
      logins
        ? schema.strip()
        : schema
            .test('baseLoginTest', '', baseLoginValidator)
            .required('common::validation_messages.required'),
    ),
    email: Yup.string()
      .test('emailValidator', 'common::validation_messages.incorrect_email_format', emailValidator)
      .required('common::validation_messages.required'),
    advertiser_legal_entity: Yup.string().required('common::validation_messages.required'),
    tax_number: Yup.string()
      .test(
        'tinValidator',
        'services::forms.shared_fields.advertiser.fields.advertiser_info.tax_number.validation_messages.incorrect_tin_number',
        tinValidator,
      )
      .required('common::validation_messages.required'),
    budget: Yup.string()
      .test({
        name: 'min_max',
        test: function (value, ctx) {
          const { path } = this

          if (+value >= BUDGET_MIN) {
            return true
          } else {
            return ctx.createError({
              path,
              message: i18n.t(
                'services::forms.register_account.sber-ads.fields.budget.errors.min',
                {
                  number: numberWithSpace(BUDGET_MIN),
                },
              ),
            })
          }
        },
      })
      .required('common::validation_messages.required'),
  })

let CreateSberAdsAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  formHeader,
  form,
  networkId,
  autofill,
  array,
  multiLogins,
  networkType,
}) => {
  const { t } = useTranslation()

  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)
    }
  }, [networkId])

  return (
    <>
      {formHeader}

      <Form
        onSubmit={handleSubmit(submitAccountData)}
        noValidate
      >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />
        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.sber-ads.fields.login.label'}
          />
        </div>
        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="email"
            required={isRequiredField(SberAdsSchema(networkType), 'email')}
            type="email"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.sber-ads.fields.email.label')}
            t={t}
          />
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="advertiser_legal_entity"
            required={isRequiredField(SberAdsSchema(networkType), 'advertiser_legal_entity')}
            fullWidth
            component={renderInputField}
            label={t(
              'services::forms.register_account.sber-ads.fields.advertiser_legal_entity.label',
            )}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t(
                'services::forms.register_account.sber-ads.fields.advertiser_legal_entity.tooltip',
              )}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="tax_number"
            required={isRequiredField(SberAdsSchema(networkType), 'tax_number')}
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.sber-ads.fields.tax_number.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.sber-ads.fields.tax_number.tooltip')}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <Field
            name="budget"
            required={isRequiredField(SberAdsSchema(networkType), 'budget')}
            type="text"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.sber-ads.fields.budget.label')}
            t={t}
          />
          <div className={formStyles['tooltip-container']}>
            <Tooltip
              title={t('services::forms.register_account.sber-ads.fields.budget.tooltip', {
                number: numberWithSpace(RECOMMENDED_BUDGET),
              })}
              placement="top"
              arrow
            >
              <Icon
                name={'information'}
                color={'Label-Secondary'}
                size={'m'}
              />
            </Tooltip>
          </div>
        </div>

        <SubmitButtons
          formName={form}
          submitting={submitting}
          onClose={onClose}
        />
      </Form>
    </>
  )
}

CreateSberAdsAccountForm = reduxForm({
  form: 'CreateSberAdsAccountForm',
  initialValues: {
    login: '',
    email: '',
    advertiser_legal_entity: '',
    tax_number: '',
    budget: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateSberAdsAccountForm)

CreateSberAdsAccountForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  autofill: PropTypes.func,
  networkId: PropTypes.string,
  initialValues: PropTypes.object,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  formHeader: PropTypes.node,
  networkType: PropTypes.string,
}

export default CreateSberAdsAccountForm
